import React, { useRef, useCallback, useLayoutEffect, useEffect, useState, useMemo } from 'react';
import { Box, Stack, Paper, Typography } from '@mui/material';

import { SimpleSwitch, SimpleButton, TTSButton, ErrorMessage, Loading } from '@/src/ui';
import { getImageUrl, addImageRef, releaseImageRef } from '@/src/utils';
import { DynamicTextPreview, type QuestionRendererOptions, defaultOptions } from '@/src/lib';
import type {
    DragAndDropPlacement,
    QtiDragAndDropQuestionDTO,
    DragChoice,
    DropZone,
    RectDropZoneGeometry,
    CircleDropZoneGeometry,
} from '../../models';
import { submitForGrading } from '../../services';

interface DragAndDropRendererProps {
    question: QtiDragAndDropQuestionDTO | null;
    handleUpdateUserResponse?: (payload: string, userInitiated: boolean) => void;
    loadedUserResponse?: string;
    options?: QuestionRendererOptions;

    maxBoxWidth?: number;
    maxBoxHeight?: number;
}

type NormPoint = { x: number; y: number };
type UiPlacement = DragAndDropPlacement & { offsetInZone?: NormPoint };

const CHOICE_FONT_PX = 24;
const CHOICE_LINE_HEIGHT = 1.25;
// Snap constants
const SNAP_STEP = 0.005;
function snap(n: number, step = SNAP_STEP) {
    const k = Math.round(1 / step);
    return Math.max(0, Math.min(1, Math.round(n * k) / k));
}

const colorPalette = {
    darkGrey: 'rgba(0,0,0,0.5)',
    mediumGrey: 'rgba(0,0,0,0.25)',
    lightGrey: 'rgba(25,118,210,0.1)',
    background: 'rgba(255,255,255,0.9)',
};

export default function DragAndDropRenderer({
    question,
    handleUpdateUserResponse,
    loadedUserResponse,
    maxBoxWidth,
    maxBoxHeight,
    options = defaultOptions,
}: DragAndDropRendererProps) {
    const {
        disabled = false,
        hidePrompt = false,
        showLanguageToggle = false,
        showAudioButtons = false,
        showScoring = false,
        language = 'en',
    } = options;

    const [error, setError] = useState<string | null>(null);

    const BOX_MAX_WIDTH = maxBoxWidth ?? 700;
    const BOX_MAX_HEIGHT = maxBoxHeight ?? 600;

    const designW = question?.designCanvas.width ?? 1;
    const designH = question?.designCanvas.height ?? 1;

    const [internalLanguage, setInternalLanguage] = useState<'en' | 'es'>(language);

    // Use if scoring needs to be handled internally
    const [userResponse, setUserResponse] = useState('');
    if (!handleUpdateUserResponse) {
        handleUpdateUserResponse = setUserResponse;
    }

    // Background image handling
    const [bgUrl, setBgUrl] = useState<string | undefined>(undefined);
    useEffect(() => {
        let prevGuid: string | null = null;
        const url =
            language === 'en' ? question?.backgroundImageUrl : question?.backgroundImageUrl_es;
        if (url?.startsWith('local://')) {
            const guid = url.slice('local://'.length);
            const obj = getImageUrl(guid);
            if (obj) {
                addImageRef(guid);
                setBgUrl(obj);
                prevGuid = guid;
            } else {
                setBgUrl(undefined);
            }
        } else {
            setBgUrl(url || undefined);
        }
        return () => {
            if (prevGuid) releaseImageRef(prevGuid);
        };
    }, [question?.backgroundImageUrl, question?.backgroundImageUrl_es, language]);

    // Manage placements
    const [placements, setPlacements] = useState<UiPlacement[]>([]);
    const userInteractedRef = useRef(false);

    useEffect(() => {
        if (loadedUserResponse) {
            try {
                const parsed = JSON.parse(loadedUserResponse);
                if (parsed.placements) {
                    setPlacements(parsed.placements);
                }
            } catch {
                // ignore
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (placements && placements.length > 0) {
            handleUpdateUserResponse(
                JSON.stringify({
                    placements: placements.map((p) => ({
                        zoneId: p.zoneId,
                        choiceId: p.choiceId,
                    })),
                }),
                userInteractedRef.current
            );
        } else {
            handleUpdateUserResponse('', userInteractedRef.current);
        }
        userInteractedRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placements]);

    // Derive maps for quick lookup
    const choicePlacedCount = useMemo(() => {
        const map = new Map<string, number>();
        placements.forEach((p) => map.set(p.choiceId, (map.get(p.choiceId) ?? 0) + 1));
        return map;
    }, [placements]);

    const zonePlaced = useMemo(() => {
        const m = new Map<string, string[]>();
        placements.forEach((p) => {
            if (!m.has(p.zoneId)) m.set(p.zoneId, []);
            m.get(p.zoneId)!.push(p.choiceId);
        });
        return m;
    }, [placements]);

    // Drag helpers
    const onDragStart = useCallback(
        (e: React.DragEvent, choiceId: string) => {
            if (disabled) return;
            e.dataTransfer.setData('application/x-choice-id', choiceId);
            e.dataTransfer.effectAllowed = 'move';
        },
        [disabled]
    );

    const canDrop = useCallback(
        (zone: DropZone, choiceId: string) => {
            if (!question) return false;
            const zArr = zonePlaced.get(zone.id) ?? [];
            if (zArr.length >= zone.maxItems) return false;

            const choice = question.choices.find((c) => c.id === choiceId);
            if (!choice) return false;

            const used = choicePlacedCount.get(choiceId) ?? 0;
            if (used >= (choice.maxPlacements ?? 1)) return false;

            if (zone.accepts && zone.accepts.length > 0) {
                if (!zone.accepts.includes(choiceId)) return false;
            }
            return true;
        },
        [choicePlacedCount, question, zonePlaced]
    );

    const removePlacement = useCallback(
        (zoneId: string, choiceId: string, indexInZone: number) => {
            if (disabled) return;
            setPlacements((prev) => {
                let count = -1;
                return prev.filter((p) => {
                    if (p.zoneId !== zoneId || p.choiceId !== choiceId) return true;
                    count++;
                    return count !== indexInZone;
                });
            });
            userInteractedRef.current = true;
        },
        [disabled]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }, []);

    // Responsive scaling
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            setContainerWidth(Math.floor(entry.contentRect.width));
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    const fitByWidth = containerWidth > 0 ? containerWidth / designW : 1;
    const fitByHeight = BOX_MAX_HEIGHT / designH;
    const renderScale = Math.min(1, fitByWidth, fitByHeight);

    const renderW = Math.round(designW * renderScale);
    const renderH = Math.round(designH * renderScale);

    // Handle drop at the canvas level instead of drop zone level
    const svgRef = useRef<SVGSVGElement>(null);

    const handleDropOnCanvas = useCallback(
        (e: React.DragEvent<SVGSVGElement>) => {
            e.preventDefault();
            const choiceId = e.dataTransfer.getData('application/x-choice-id');
            if (!choiceId || !question) return;

            const svg = svgRef.current;
            if (!svg) return;

            const rect = svg.getBoundingClientRect();
            const px = e.clientX - rect.left;
            const py = e.clientY - rect.top;

            // convert to DESIGN space
            const dx = px / renderScale;
            const dy = py / renderScale;

            const zonesDesc = (question.zones || [])
                .slice()
                .sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));

            for (const z of zonesDesc) {
                const g = language === 'en' ? z.geometry : z.geometry_es;
                let inside = false,
                    zx = 0,
                    zy = 0,
                    zw = 0,
                    zh = 0,
                    cx = 0,
                    cy = 0,
                    r = 0;

                if (g.type === 'rect') {
                    zx = (g.x ?? 0) * designW;
                    zy = (g.y ?? 0) * designH;
                    zw = (g.width ?? 0) * designW;
                    zh = (g.height ?? 0) * designH;
                    inside = pointInRect(dx, dy, zx, zy, zw, zh);
                } else {
                    cx = (g.cx ?? 0) * designW;
                    cy = (g.cy ?? 0) * designH;
                    r = (g.r ?? 0) * designW;
                    inside = pointInCircle(dx, dy, cx, cy, r);
                    zx = cx - r;
                    zy = cy - r;
                    zw = r * 2;
                    zh = r * 2;
                }

                if (!inside) continue;
                if (!canDrop(z, choiceId)) continue;

                let off: NormPoint;
                if (z.snap === 'center') off = { x: 0.5, y: 0.5 };
                else if (z.snap === 'origin') off = { x: 0, y: 0 };
                else {
                    const nx = (dx - zx) / Math.max(1e-6, zw);
                    const ny = (dy - zy) / Math.max(1e-6, zh);
                    off = { x: snap(nx), y: snap(ny) };
                }

                setPlacements((prev) => {
                    const zArr = prev.filter((p) => p.zoneId === z.id);
                    if (zArr.length >= z.maxItems) return prev;

                    const choice = question.choices.find((c) => c.id === choiceId);
                    const used = prev.filter((p) => p.choiceId === choiceId).length;
                    if (choice && used >= (choice.maxPlacements ?? 1)) return prev;

                    return [...prev, { choiceId, zoneId: z.id, offsetInZone: off }];
                });
                userInteractedRef.current = true;
                return; // handled
            }
        },
        [question, canDrop, designW, designH, renderScale, language]
    );

    // Early return after hooks if loading failed
    if (!question) return 'Unable to load question';

    return (
        <Stack spacing={2}>
            {showLanguageToggle && question.meta.isSpanishAllowed && (
                <div className="flex flex-row gap-x-8">
                    <div className="ml-auto">
                        <SimpleSwitch
                            name="toggleSpanish"
                            checked={internalLanguage === 'es'}
                            onChange={(e) => setInternalLanguage(e.target.checked ? 'es' : 'en')}
                            label="View Spanish"
                        />
                    </div>
                </div>
            )}
            {!hidePrompt && (
                <div
                    className="mb-4 flex flex-row items-end"
                    onContextMenu={(e) => {
                        if (options.onRendererContextMenu) {
                            e.preventDefault();
                            e.stopPropagation();
                            options.onRendererContextMenu(
                                e,
                                {
                                    language: internalLanguage,
                                    text: question.prompt,
                                    text_es: question.prompt_es,
                                },
                                []
                            );
                        }
                    }}
                >
                    <DynamicTextPreview
                        content={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                    />
                    {showAudioButtons && (
                        <TTSButton
                            text={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                            onError={setError}
                        />
                    )}
                </div>
            )}

            <Stack direction="column" spacing={2}>
                <Paper elevation={1} sx={{ p: 1 }}>
                    <Box ref={containerRef} sx={{ width: '100%', maxWidth: `${BOX_MAX_WIDTH}px` }}>
                        <Box
                            sx={{
                                position: 'relative',
                                width: `${renderW}px`,
                                height: `${renderH}px`,
                                backgroundImage: bgUrl ? `url(${bgUrl})` : 'none',
                                backgroundSize: '100% 100%',
                                backgroundRepeat: 'no-repeat',
                                border: `1px solid ${colorPalette.lightGrey}`,
                                overflow: 'visible',
                                userSelect: 'none',
                            }}
                            onDragOver={handleDragOver}
                        >
                            <svg
                                ref={svgRef}
                                width={renderW}
                                height={renderH}
                                style={{ display: 'block', position: 'relative', zIndex: 1 }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleDropOnCanvas}
                            >
                                {question.zones
                                    .slice()
                                    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                                    .map((z) => {
                                        const geometry =
                                            internalLanguage === 'en' ? z.geometry : z.geometry_es;

                                        if (geometry.type === 'rect') {
                                            const g = geometry as RectDropZoneGeometry;
                                            const x = (g.x ?? 0) * designW * renderScale;
                                            const y = (g.y ?? 0) * designH * renderScale;
                                            const w = (g.width ?? 0) * designW * renderScale;
                                            const h = (g.height ?? 0) * designH * renderScale;
                                            const r = Math.max(
                                                0,
                                                (g.cornerRadius ?? 0) * Math.min(w, h)
                                            );
                                            return (
                                                <g key={z.id}>
                                                    <rect
                                                        x={x}
                                                        y={y}
                                                        width={w}
                                                        height={h}
                                                        rx={r}
                                                        ry={r}
                                                        fill={colorPalette.lightGrey}
                                                        stroke={colorPalette.darkGrey}
                                                        strokeWidth={2}
                                                    />
                                                </g>
                                            );
                                        } else if (geometry.type === 'circle') {
                                            const g = geometry as CircleDropZoneGeometry;
                                            const cx = (g.cx ?? 0) * designW * renderScale;
                                            const cy = (g.cy ?? 0) * designH * renderScale;
                                            const r = (g.r ?? 0) * designW * renderScale;
                                            return (
                                                <g key={z.id}>
                                                    <circle
                                                        cx={cx}
                                                        cy={cy}
                                                        r={r}
                                                        fill={colorPalette.lightGrey}
                                                        stroke={colorPalette.darkGrey}
                                                        strokeWidth={2}
                                                    />
                                                </g>
                                            );
                                        }
                                        return null;
                                    })}
                            </svg>

                            <div
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    width: designW,
                                    height: designH,
                                    transform: `scale(${renderScale})`,
                                    transformOrigin: 'top left',
                                    pointerEvents: 'none',
                                    overflow: 'visible',
                                    zIndex: 2,
                                }}
                            >
                                {renderChipsOverlay(
                                    question.zones,
                                    placements,
                                    question.choices,
                                    designW,
                                    designH,
                                    internalLanguage,
                                    disabled,
                                    removePlacement
                                )}
                            </div>
                        </Box>
                    </Box>
                </Paper>

                {!hidePrompt && (
                    <Paper elevation={1} sx={{ p: 1 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Choices
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1.5,
                                alignItems: 'center',
                            }}
                        >
                            {question.choices.map((c) => {
                                const used = choicePlacedCount.get(c.id) ?? 0;
                                const remaining = Math.max(0, (c.maxPlacements ?? 1) - used);
                                const text =
                                    internalLanguage === 'en'
                                        ? (c.value ?? '')
                                        : (c.value_es ?? '');

                                return (
                                    <Box
                                        key={c.id}
                                        draggable={remaining > 0 && !disabled}
                                        onDragStart={(e) => onDragStart(e, c.id)}
                                        sx={{
                                            opacity: remaining > 0 && !disabled ? 1 : 0.4,
                                            cursor:
                                                remaining > 0 && !disabled ? 'grab' : 'not-allowed',
                                        }}
                                        title={
                                            remaining > 0
                                                ? `${remaining} remaining`
                                                : 'No remaining placements'
                                        }
                                    >
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                transform: `scale(${renderScale})`,
                                                transformOrigin: 'left center',
                                            }}
                                        >
                                            <span
                                                style={{
                                                    background: colorPalette.background,
                                                    border: `1px solid ${colorPalette.darkGrey}`,
                                                    borderRadius: 12,
                                                    padding: '6px 10px',
                                                    boxShadow: `0 1px 2px ${colorPalette.lightGrey}`,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    maxWidth: 320,
                                                    whiteSpace: 'nowrap',
                                                    lineHeight: CHOICE_LINE_HEIGHT,
                                                    fontSize: CHOICE_FONT_PX,
                                                }}
                                                onContextMenu={(e) => {
                                                    if (options.onRendererContextMenu) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        options.onRendererContextMenu(
                                                            e,
                                                            {
                                                                language: internalLanguage,
                                                                text: c.value || '',
                                                                text_es: c.value_es || '',
                                                            },
                                                            []
                                                        );
                                                    }
                                                }}
                                            >
                                                <DynamicTextPreview content={text} />
                                            </span>
                                        </span>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                )}
            </Stack>

            {showScoring && <DragAndDropScoring question={question} userResponse={userResponse} />}
            <ErrorMessage error={error} />
        </Stack>
    );
}

function renderChipsOverlay(
    zones: DropZone[],
    placements: UiPlacement[],
    choices: DragChoice[],
    designW: number,
    designH: number,
    language: 'en' | 'es',
    disabled: boolean,
    onRemove: (zoneId: string, choiceId: string, indexInZone: number) => void
) {
    const nodes: React.ReactNode[] = [];

    function ChoicePill({ text, onRemove }: { text: string; onRemove: () => void }) {
        return (
            <span
                style={{
                    position: 'relative',
                    display: 'inline-flex',
                    pointerEvents: 'none',
                    flex: '0 0 auto',
                }}
                draggable={false}
            >
                <span
                    style={{
                        background: colorPalette.background,
                        border: `1px solid ${colorPalette.mediumGrey}`,
                        borderRadius: 12,
                        padding: '6px 10px',
                        boxShadow: `0 1px 2px ${colorPalette.lightGrey}`,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        maxWidth: 320,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        flex: '0 0 auto',
                    }}
                    draggable={false}
                >
                    <div
                        style={{
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            pointerEvents: 'none',
                            lineHeight: CHOICE_LINE_HEIGHT,
                            fontSize: CHOICE_FONT_PX,
                        }}
                        draggable={false}
                    >
                        <DynamicTextPreview content={text} />
                    </div>

                    <button
                        type="button"
                        aria-label="Remove"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onRemove();
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            width: 16,
                            height: 16,
                            borderRadius: 8,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            appearance: 'none',
                            border: `1px solid ${colorPalette.mediumGrey}`,
                            background: colorPalette.background,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            fontSize: 11,
                            lineHeight: 1,
                            color: colorPalette.darkGrey,
                            boxShadow: `0 1px 2px ${colorPalette.lightGrey}`,
                            pointerEvents: 'auto',
                            zIndex: 1,
                        }}
                    >
                        ×
                    </button>
                </span>
            </span>
        );
    }

    for (const zone of zones) {
        const inZone = placements.filter((p) => p.zoneId === zone.id);
        if (inZone.length === 0) continue;

        // Geometry in design (original) units
        let x = 0,
            y = 0,
            w = 0,
            h = 0,
            cx = 0,
            cy = 0,
            r = 0;
        if (zone.geometry.type === 'rect') {
            const g = zone.geometry as RectDropZoneGeometry;
            x = (g.x ?? 0) * designW;
            y = (g.y ?? 0) * designH;
            w = (g.width ?? 0) * designW;
            h = (g.height ?? 0) * designH;
            cx = x + w / 2;
            cy = y + h / 2;
        } else {
            const g = zone.geometry as CircleDropZoneGeometry;
            cx = (g.cx ?? 0) * designW;
            cy = (g.cy ?? 0) * designH;
            r = (g.r ?? 0) * designW;
            x = cx - r;
            y = cy - r;
            w = r * 2;
            h = r * 2;
        }

        const perChoiceCounts = new Map<string, number>();

        if (inZone.length > 1) {
            nodes.push(
                <div
                    key={`${zone.id}-group`}
                    style={{
                        position: 'absolute',
                        left: cx,
                        top: cy,
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexWrap: 'nowrap',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 8,
                        width: 'auto',
                        maxWidth: 'none',
                        overflow: 'visible',
                        pointerEvents: 'none',
                        zIndex: 3,
                    }}
                >
                    {inZone.map((p, i) => {
                        const c = choices.find((cc) => cc.id === p.choiceId);
                        const txt = language === 'en' ? (c?.value ?? '') : (c?.value_es ?? '');

                        // get per-choice index for removal
                        const idxForRemove = perChoiceCounts.get(p.choiceId) ?? 0;
                        perChoiceCounts.set(p.choiceId, idxForRemove + 1);

                        return (
                            <span
                                key={`${zone.id}-${p.choiceId}-${i}`}
                                style={{ pointerEvents: 'none', flex: '0 0 auto' }}
                            >
                                <ChoicePill
                                    text={txt}
                                    onRemove={() => onRemove(zone.id, p.choiceId, idxForRemove)}
                                />
                            </span>
                        );
                    })}
                </div>
            );
        } else {
            const p = inZone[0];
            const c = choices.find((cc) => cc.id === p.choiceId);
            const off = p.offsetInZone ?? { x: 0.5, y: 0.5 };
            const px = x + (off.x ?? 0.5) * w;
            const py = y + (off.y ?? 0.5) * h;
            const txt = language === 'en' ? (c?.value ?? '') : (c?.value_es ?? '');

            const idxForRemove = perChoiceCounts.get(p.choiceId) ?? 0;
            perChoiceCounts.set(p.choiceId, idxForRemove + 1);

            nodes.push(
                <span
                    key={`${zone.id}-${p.choiceId}-single`}
                    style={{
                        position: 'absolute',
                        left: px,
                        top: py,
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none',
                        zIndex: 3,
                    }}
                >
                    <ChoicePill
                        text={txt}
                        onRemove={() => onRemove(zone.id, p.choiceId, idxForRemove)}
                    />
                </span>
            );
        }
    }

    return nodes;
}

function pointInRect(px: number, py: number, x: number, y: number, w: number, h: number) {
    return px >= x && px <= x + w && py >= y && py <= y + h;
}
function pointInCircle(px: number, py: number, cx: number, cy: number, r: number) {
    const dx = px - cx,
        dy = py - cy;
    return dx * dx + dy * dy <= r * r;
}

// SCORING //
interface DragAndDropScoringProps {
    question: QtiDragAndDropQuestionDTO;
    userResponse: string;
}

function DragAndDropScoring({ question, userResponse }: DragAndDropScoringProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isCorrect, setIsCorrect] = useState(false);
    const [answerChecked, setAnswerChecked] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        setAnswerChecked(false);
        setMessage('');
    }, [userResponse]);

    const handleCheckAnswer = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const { isCorrect: isDeterminedCorrect, message } = await submitForGrading(
                question,
                userResponse
            );
            setIsCorrect(isDeterminedCorrect);
            setMessage(message ?? '');
            setAnswerChecked(true);
        } catch {
            setError('Failed to check answer.');
            setIsCorrect(false);
        } finally {
            setLoading(false);
        }
    }, [question, userResponse]);

    if (question.meta.isHandScored === true) return null;

    const parsedResponse = userResponse ? JSON.parse(userResponse) : null;
    if (!parsedResponse) return null;

    const placements = parsedResponse?.placements || [];

    return (
        <div className="mt-4 flex items-center space-x-4">
            <div>
                <SimpleButton
                    color="success"
                    onClick={handleCheckAnswer}
                    disabled={!placements.length || answerChecked || loading}
                    variant="contained"
                    className="px-4 py-1 rounded disabled:opacity-50"
                >
                    Check Answer
                </SimpleButton>
                {loading && <Loading message="Checking answer..." />}
            </div>

            {error ? (
                <ErrorMessage error={error} />
            ) : (
                <span
                    className={`${isCorrect ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}`}
                >
                    {answerChecked && placements.length ? message : '\u00A0'}
                </span>
            )}
        </div>
    );
}
